/**
 * useToastConfirm.ts -- Toast-based undoable mutation pattern.
 *
 * PRD SS18.6: Returns a `showWithUndo` function that displays an in-document
 * toast with an undo affordance. After `undoMs` (default 5000ms), the toast
 * auto-dismisses. If the user clicks "Undo", the `onUndo` callback fires.
 *
 * Implementation uses a DOM-mounted toast container for simplicity and
 * framework independence. All styling is inline to avoid CSS coupling.
 */
import { useCallback, useRef } from "react";

interface ShowWithUndoArgs {
  message: string;
  undoMs?: number;
  onUndo: () => Promise<void>;
}

function getOrCreateContainer(): HTMLDivElement {
  const id = "cc-toast-container";
  let container = document.getElementById(id) as HTMLDivElement | null;
  if (!container) {
    container = document.createElement("div");
    container.id = id;
    container.style.cssText = [
      "position: fixed",
      "bottom: 24px",
      "right: 24px",
      "z-index: 2000",
      "display: flex",
      "flex-direction: column",
      "gap: 8px",
      "pointer-events: none",
    ].join(";");
    document.body.appendChild(container);
  }
  return container;
}

function createToastElement(message: string): HTMLDivElement {
  const el = document.createElement("div");
  el.style.cssText = [
    "display: flex",
    "align-items: center",
    "gap: 12px",
    "padding: 12px 16px",
    "background-color: var(--color-surface-sumi, #1A1614)",
    "color: #FFFFFF",
    "border-radius: 8px",
    "font-family: var(--font-display)",
    "font-size: 14px",
    "box-shadow: 0 4px 16px rgba(0,0,0,0.2)",
    "pointer-events: auto",
    "max-width: 400px",
  ].join(";");
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");

  const textSpan = document.createElement("span");
  textSpan.style.flex = "1";
  textSpan.textContent = message;
  el.appendChild(textSpan);

  return el;
}

export function useToastConfirm(): {
  showWithUndo: (args: ShowWithUndoArgs) => void;
} {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showWithUndo = useCallback(
    ({ message, undoMs = 5000, onUndo }: ShowWithUndoArgs) => {
      const container = getOrCreateContainer();
      const toast = createToastElement(message);

      // Undo button
      const undoBtn = document.createElement("button");
      undoBtn.textContent = "Undo";
      undoBtn.style.cssText = [
        "background: none",
        "border: none",
        "color: var(--color-surface-sora, #5CB8DB)",
        "font-weight: 600",
        "cursor: pointer",
        "font-size: 14px",
        "font-family: inherit",
        "padding: 0",
        "text-decoration: underline",
      ].join(";");
      toast.appendChild(undoBtn);

      container.appendChild(toast);

      const dismiss = () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      };

      undoBtn.addEventListener("click", () => {
        dismiss();
        void onUndo();
      });

      // Auto-dismiss after undoMs
      timeoutRef.current = setTimeout(dismiss, undoMs);
    },
    [],
  );

  return { showWithUndo };
}

export default useToastConfirm;
